// FirewallVipIPv4.tsx
import { useState, useEffect, useCallback } from "react";
import {
    RefreshCw, Loader2, Search, Waypoints,
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    ArrowUp, ArrowDown, X, ArrowRight, Globe, Server,
    Shield, Tag, Filter, Cpu
} from "lucide-react";
import { showError } from "../../../../../utils/toast";
import baseapi from "../../../../../services/baseapi";
import { ItemsPerPageDropdown } from "../../../../../components/ItemsPerPageDropdown ";


// ── Types ─────────────────────────────────────────────────────────────────────
interface MappedIPItem { range: string; q_origin_key: string; }
interface SrcFilterItem { range: string; q_origin_key: string; }
interface RealServerItem { id: number; ip: string; port: number; status: string; }

interface VipIPv4Item {
    id: number;
    created: string;
    updated: string;
    asset_id: number;
    firewall_type: string;
    name: string;
    uuid: string;
    type: string;
    extip: string;
    extintf: string;
    extport: string;
    mappedport: string;
    protocol: string;
    portforward: string;
    nat44: string;
    nat46: string;
    arp_reply: string;
    comment: string;
    mappedip: string;
    src_filter: string;
    realservers: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const parseJSON = <T,>(raw: any): T[] => {
    if (typeof raw === "object" && raw !== null) {
        if (Array.isArray(raw)) return raw as T[];
        return [raw] as T[];
    }
    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
        return parsed ? [parsed] : [];
    } catch { return []; }
};

const FLAG_CHIP = (value: string) => (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold border uppercase ${value === "enable"
        ? "bg-green-500/10 border-green-500/20 text-green-400"
        : "bg-gray-700/40 border-gray-600/30 text-gray-500"
        }`}>
        {value}
    </span>
);

// ── Sort Icon ─────────────────────────────────────────────────────────────────
const SortIcon = ({ column, sortConfig }: {
    column: string;
    sortConfig: { key: string; direction: "asc" | "desc" } | null;
}) => {
    if (sortConfig?.key !== column) return (
        <div className="opacity-0 group-hover:opacity-40 transition-opacity">
            <ArrowUp size={10} className="text-slate-400" />
        </div>
    );
    return sortConfig.direction === "asc"
        ? <ArrowUp size={10} className="text-blue-400" />
        : <ArrowDown size={10} className="text-blue-400" />;
};

// ── Right Drawer ──────────────────────────────────────────────────────────────
function VipDrawer({ item, onClose }: { item: VipIPv4Item; onClose: () => void }) {
    const mappedIPs = parseJSON<MappedIPItem>(item.mappedip);
    const srcFilters = parseJSON<SrcFilterItem>(item.src_filter);
    const realServers = parseJSON<RealServerItem>(item.realservers);

    const DetailRow = ({ label, value, color = "text-gray-300" }: { label: string; value: any; color?: string }) => (
        <div className="flex justify-between items-center py-1 border-b border-gray-700/40 last:border-0">
            <span className="text-[9px] font-bold text-gray-500 uppercase tracking-widest">{label}</span>
            <span className={`text-[10px] font-bold ${color}`}>{value ?? "—"}</span>
        </div>
    );

    return (
        <>
            <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
            <div className="fixed top-0 right-0 h-full w-[30%] min-w-[320px] bg-gray-900 border-l border-gray-700 z-55 flex flex-col shadow-2xl overflow-hidden">

                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 bg-gray-800/60 shrink-0">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <div className="w-7 h-7 rounded-lg bg-violet-600/20 border border-violet-500/30 flex items-center justify-center shrink-0">
                            <Waypoints size={13} className="text-violet-400" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[12px] font-black text-white truncate">{item.name}</p>
                            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                <span className="px-1.5 py-0.5 text-[8px] font-bold border rounded uppercase bg-violet-500/10 border-violet-500/20 text-violet-400">
                                    {item.type}
                                </span>
                                <span className="px-1.5 py-0.5 text-[8px] font-bold border rounded uppercase bg-blue-500/10 border-blue-500/20 text-blue-400">
                                    {item.protocol}
                                </span>
                            </div>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-gray-700 transition-all shrink-0 ml-2">
                        <X size={14} />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto custom-scroll p-4 space-y-5">

                    {/* Port Forwarding Flow */}
                    <div className="bg-gray-800/50 border border-gray-700/60 rounded-lg p-3">
                        <p className="text-[9px] font-bold text-gray-500 uppercase tracking-widest mb-3">Port Forward Flow</p>
                        <div className="flex items-center gap-2 flex-wrap">
                            {/* External */}
                            <div className="flex-1 min-w-0 bg-orange-500/5 border border-orange-500/20 rounded-lg p-2.5 text-center">
                                <p className="text-[8px] font-bold text-orange-400 uppercase tracking-widest mb-1">External</p>
                                <p className="font-mono text-[12px] font-black text-orange-300">{item.extip}</p>
                                <p className="font-mono text-[10px] text-orange-400/70 mt-0.5">:{item.extport}</p>
                                <p className="text-[8px] text-gray-500 mt-1">{item.extintf}</p>
                            </div>
                            <ArrowRight size={16} className="text-gray-600 shrink-0" />
                            {/* Mapped */}
                            <div className="flex-1 min-w-0 bg-blue-500/5 border border-blue-500/20 rounded-lg p-2.5 text-center">
                                <p className="text-[8px] font-bold text-blue-400 uppercase tracking-widest mb-1">Mapped</p>
                                {mappedIPs.map((m, i) => (
                                    <p key={i} className="font-mono text-[12px] font-black text-blue-300">{m.range}</p>
                                ))}
                                <p className="font-mono text-[10px] text-blue-400/70 mt-0.5">:{item.mappedport}</p>
                            </div>
                        </div>
                    </div>

                    {/* Flags */}
                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-5 h-5 rounded bg-green-600/20 border border-green-500/30 flex items-center justify-center">
                                <Shield size={10} className="text-green-400" />
                            </div>
                            <h3 className="text-[10px] font-black text-green-400 uppercase tracking-widest">Flags</h3>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            {[
                                ["Port Forward", item.portforward === "enable"],
                                ["NAT44", item.nat44 === "enable"],
                                ["NAT46", item.nat46 === "enable"],
                                ["ARP Reply", item.arp_reply === "enable"],
                            ].map(([label, enabled]) => (
                                <div key={label as string} className={`flex items-center justify-between px-2.5 py-2 rounded-lg border ${enabled
                                    ? "bg-green-500/5 border-green-500/20"
                                    : "bg-gray-800/40 border-gray-700/50"
                                    }`}>
                                    <span className="text-[9px] font-bold text-gray-400 uppercase">{label as string}</span>
                                    <span className={`text-[9px] font-black ${enabled ? "text-green-400" : "text-gray-600"}`}>
                                        {enabled ? "ON" : "OFF"}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Mapped IPs */}
                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-5 h-5 rounded bg-blue-600/20 border border-blue-500/30 flex items-center justify-center">
                                <Server size={10} className="text-blue-400" />
                            </div>
                            <h3 className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Mapped IPs</h3>
                            <span className="px-1.5 py-0.5 text-[8px] font-bold bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded">{mappedIPs.length}</span>
                        </div>
                        {mappedIPs.length === 0 ? (
                            <div className="p-3 bg-gray-800/40 border border-gray-700/50 rounded-lg">
                                <span className="text-[10px] text-gray-500">No mapped IPs</span>
                            </div>
                        ) : (
                            <div className="space-y-1.5">
                                {mappedIPs.map((m, i) => (
                                    <div key={i} className="flex items-center gap-2 px-3 py-2 bg-blue-500/5 border border-blue-500/15 rounded-lg">
                                        <Server size={10} className="text-blue-400 shrink-0" />
                                        <span className="font-mono text-[11px] font-bold text-blue-300">{m.range}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Source Filters */}
                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-5 h-5 rounded bg-yellow-600/20 border border-yellow-500/30 flex items-center justify-center">
                                <Filter size={10} className="text-yellow-400" />
                            </div>
                            <h3 className="text-[10px] font-black text-yellow-400 uppercase tracking-widest">Source Filters</h3>
                            <span className="px-1.5 py-0.5 text-[8px] font-bold bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 rounded">{srcFilters.length}</span>
                        </div>
                        {srcFilters.length === 0 ? (
                            <div className="p-3 bg-gray-800/40 border border-gray-700/50 rounded-lg">
                                <span className="text-[10px] text-gray-500">No source filters (allow all)</span>
                            </div>
                        ) : (
                            <div className="space-y-1.5">
                                {srcFilters.map((f, i) => (
                                    <div key={i} className="flex items-center gap-2 px-3 py-2 bg-yellow-500/5 border border-yellow-500/15 rounded-lg">
                                        <Filter size={9} className="text-yellow-400 shrink-0" />
                                        <span className="font-mono text-[11px] font-bold text-yellow-300">{f.range}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Real Servers */}
                    {realServers.length > 0 && (
                        <div>
                            <div className="flex items-center gap-2 mb-3">
                                <div className="w-5 h-5 rounded bg-purple-600/20 border border-purple-500/30 flex items-center justify-center">
                                    <Cpu size={10} className="text-purple-400" />
                                </div>
                                <h3 className="text-[10px] font-black text-purple-400 uppercase tracking-widest">Real Servers</h3>
                                <span className="px-1.5 py-0.5 text-[8px] font-bold bg-purple-500/10 border border-purple-500/20 text-purple-400 rounded">{realServers.length}</span>
                            </div>
                            <div className="space-y-1.5">
                                {realServers.map((s, i) => (
                                    <div key={i} className="flex items-center justify-between px-3 py-2 bg-purple-500/5 border border-purple-500/15 rounded-lg">
                                        <span className="font-mono text-[11px] font-bold text-purple-300">{s.ip}:{s.port}</span>
                                        <span className={`text-[9px] font-bold ${s.status === "active" ? "text-green-400" : "text-gray-500"}`}>{s.status}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Details */}
                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-5 h-5 rounded bg-gray-600/30 border border-gray-600/40 flex items-center justify-center">
                                <Tag size={10} className="text-gray-400" />
                            </div>
                            <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Details</h3>
                        </div>
                        <div className="bg-gray-800/40 border border-gray-700/50 rounded-lg p-3 space-y-1">
                            <DetailRow label="Type" value={item.type} />
                            <DetailRow label="Protocol" value={item.protocol} />
                            <DetailRow label="Interface" value={item.extintf} />
                            {item.comment && <DetailRow label="Comment" value={item.comment} color="text-gray-400" />}
                        </div>
                        <div className="mt-3 bg-gray-800/40 border border-gray-700/50 rounded-lg p-3">
                            <p className="text-[9px] font-bold text-gray-500 uppercase tracking-widest mb-1">UUID</p>
                            <p className="font-mono text-[9px] text-gray-500 break-all">{item.uuid}</p>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function FirewallVipIPv4({ asset_id, client_id }: { asset_id: any, client_id: string }) {
    const [items, setItems] = useState<VipIPv4Item[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [selected, setSelected] = useState<VipIPv4Item | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(15);
    const [totalRecords, setTotalRecords] = useState(0);
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);

    useEffect(() => {
        const t = setTimeout(() => { setDebouncedSearch(searchTerm); setCurrentPage(1); }, 400);
        return () => clearTimeout(t);
    }, [searchTerm]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const queryObj: any = {};
            if (asset_id) queryObj["asset_id"] = asset_id;
            if (client_id) queryObj["client_id"] = client_id;
            if (debouncedSearch) queryObj.$or = [
                { name: { $ilike: `%${debouncedSearch}%` } },
                { extip: { $ilike: `%${debouncedSearch}%` } },
                { type: { $ilike: `%${debouncedSearch}%` } },
            ];
            const params: any = {
                query: JSON.stringify(queryObj),
                skip: (currentPage - 1) * limit,
                limit,
            };
            if (sortConfig) { params.sort_by = sortConfig.key; params.sort_order = sortConfig.direction; }
            const res = await baseapi.get("/firewall_vip_ipv4", { params });
            if (res.data.status) {
                setItems(res.data.data);
                setTotalRecords(res.data.total ?? res.data.data.length);
            } else showError("Failed to load VIP IPv4 entries");
        } catch (e: any) { showError(e.message); }
        finally { setLoading(false); }
    }, [asset_id, debouncedSearch, currentPage, limit, sortConfig]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleSort = (key: string) => {
        setSortConfig(prev =>
            prev?.key === key
                ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
                : { key, direction: "asc" }
        );
        setCurrentPage(1);
    };

    const totalPages = Math.ceil(totalRecords / limit);
    const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * limit + 1;
    const endRecord = Math.min(currentPage * limit, totalRecords);

    const Th = ({ label, col, right = false }: { label: string; col: string; right?: boolean }) => (
        <th
            className={`px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest cursor-pointer group whitespace-nowrap ${right ? "text-right" : ""}`}
            onClick={() => handleSort(col)}
        >
            <div className={`flex items-center gap-1 ${right ? "justify-end" : ""}`}>
                {label} <SortIcon column={col} sortConfig={sortConfig} />
            </div>
        </th>
    );

    return (
        <div className="flex flex-col bg-transparent text-gray-200">

            {selected && <VipDrawer item={selected} onClose={() => setSelected(null)} />}

            {/* Action Bar */}
            <div className="py-2 flex items-center justify-between gap-4 shrink-0 mb-2">
                <div className="flex items-center gap-2">
                    <div className="bg-violet-600/20 p-1.5 rounded-lg border border-violet-500/30">
                        <Waypoints size={16} className="text-violet-400" />
                    </div>
                    <h1 className="text-lg font-bold text-white tracking-wide">VIP IPv4</h1>
                    <span className="text-[10px] font-bold text-gray-500 bg-gray-800 border border-gray-700 px-2 py-0.5 rounded">
                        {totalRecords} entries
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative w-56 group">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 group-focus-within:text-violet-400 transition-colors" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search name, IP, type..."
                            className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-white placeholder-gray-500 focus:outline-none focus:border-violet-500/50 transition-all"
                        />
                    </div>
                    <button
                        onClick={fetchData}
                        disabled={loading}
                        className="p-1.5 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700 transition-all group shrink-0"
                    >
                        <RefreshCw size={14} className={loading ? "animate-spin" : "group-hover:text-violet-400"} />
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="bg-gray-800/40 border border-gray-700 rounded-xl overflow-hidden shadow-2xl">
                <div className="overflow-x-auto custom-scroll">
                    <table className="w-full text-left text-xs border-collapse">
                        <thead className="bg-gray-800/80 sticky top-0 z-10">
                            <tr className="border-b border-gray-700/50">
                                <Th label="Name" col="name" />
                                <Th label="Type" col="type" />
                                <Th label="External IP" col="extip" />
                                <Th label="Ext Port" col="extport" right />
                                <Th label="Mapped IP" col="mappedip" />
                                <Th label="Mapped Port" col="mappedport" right />
                                <Th label="Protocol" col="protocol" />
                                <Th label="Interface" col="extintf" />
                                <Th label="NAT44" col="nat44" />
                                <Th label="Port Fwd" col="portforward" />
                                <Th label="ARP Reply" col="arp_reply" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700/30">
                            {loading ? (
                                <tr>
                                    <td colSpan={11} className="py-16 text-center">
                                        <div className="flex items-center justify-center gap-2 text-gray-500">
                                            <Loader2 className="w-4 h-4 animate-spin text-violet-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest">Loading VIP IPv4...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : items.length === 0 ? (
                                <tr>
                                    <td colSpan={11} className="py-16 text-center">
                                        <div className="flex flex-col items-center gap-2 opacity-40">
                                            <Waypoints size={24} className="text-gray-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest">No VIP entries found</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                items.map((item) => {
                                    const mappedIPs = parseJSON<MappedIPItem>(item.mappedip);
                                    return (
                                        <tr
                                            key={item.id}
                                            onClick={() => setSelected(item)}
                                            className="hover:bg-gray-700/50 transition-colors cursor-pointer"
                                            title="Click to view details"
                                        >
                                            {/* Name */}
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                <div className="flex items-center gap-2">

                                                    <div>
                                                        <p className="text-[11px] font-bold text-gray-100">{item.name}</p>
                                                        {item.comment && (
                                                            <p className="text-[9px] text-gray-500 truncate max-w-[140px]">{item.comment}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Type */}
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold border uppercase bg-violet-500/10 border-violet-500/20 text-violet-400">
                                                    {item.type}
                                                </span>
                                            </td>

                                            {/* External IP */}
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                <div className="flex items-center gap-1">
                                                    <Globe size={10} className="text-orange-500/60 shrink-0" />
                                                    <span className="font-mono text-[11px] font-bold text-orange-300">{item.extip}</span>
                                                </div>
                                            </td>

                                            {/* Ext Port */}
                                            <td className="px-4 py-2.5 whitespace-nowrap text-right">
                                                <span className="font-mono text-[11px] font-bold text-orange-400/80">:{item.extport}</span>
                                            </td>

                                            {/* Mapped IP */}
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                {mappedIPs.length === 0 ? (
                                                    <span className="text-gray-600 text-[10px]">—</span>
                                                ) : (
                                                    <div className="flex items-center gap-1">
                                                        <ArrowRight size={9} className="text-gray-600 shrink-0" />
                                                        <span className="font-mono text-[11px] font-bold text-blue-300">{mappedIPs[0].range}</span>
                                                        {mappedIPs.length > 1 && (
                                                            <span className="text-[9px] text-gray-500">+{mappedIPs.length - 1}</span>
                                                        )}
                                                    </div>
                                                )}
                                            </td>

                                            {/* Mapped Port */}
                                            <td className="px-4 py-2.5 whitespace-nowrap text-right">
                                                <span className="font-mono text-[11px] font-bold text-blue-400/80">:{item.mappedport}</span>
                                            </td>

                                            {/* Protocol */}
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold border uppercase bg-blue-500/10 border-blue-500/20 text-blue-400">
                                                    {item.protocol}
                                                </span>
                                            </td>

                                            {/* Interface */}
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                <span className="text-[10px] font-mono text-gray-400">{item.extintf}</span>
                                            </td>

                                            {/* NAT44 */}
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                {FLAG_CHIP(item.nat44)}
                                            </td>

                                            {/* Port Forward */}
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                {FLAG_CHIP(item.portforward)}
                                            </td>

                                            {/* ARP Reply */}
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                {FLAG_CHIP(item.arp_reply)}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                <div className="flex items-center justify-between px-3 py-1.5 bg-gray-800/40 border-t border-gray-700 shrink-0">
                    <div className="flex items-center gap-3">
                        <ItemsPerPageDropdown
                            value={limit}
                            onChange={(v) => { setLimit(v); setCurrentPage(1); }}
                        />
                        <span className="text-[9px] font-medium text-gray-500 tracking-widest">
                            <span className="text-gray-300">{startRecord}</span>
                            {" - "}
                            <span className="text-gray-300">{endRecord}</span>
                            <span className="mx-1 text-gray-600">/</span>
                            <span className="text-gray-300 font-bold">{totalRecords}</span>
                        </span>
                        {debouncedSearch && (
                            <button onClick={() => setSearchTerm("")}
                                className="text-[9px] text-violet-400 hover:text-violet-300 font-bold uppercase tracking-widest transition-colors">
                                Clear search
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(1)}
                            className="p-1 text-gray-400 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronsLeft size={14} />
                        </button>
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            className="p-1 text-gray-400 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronLeft size={14} />
                        </button>
                        <div className="px-2 py-0.5 bg-violet-500/10 border border-violet-500/20 text-violet-400 rounded text-[10px] font-bold mx-1">
                            {currentPage}
                        </div>
                        <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}
                            className="p-1 text-gray-400 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronRight size={14} />
                        </button>
                        <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(totalPages)}
                            className="p-1 text-gray-400 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronsRight size={14} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
